import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { choiceListDefinition } from '../../../../entity-definitions/choice-list.definition';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { ToastService } from '../../../../services/toast.service';
import { ChoiceListElementStore } from '../../../../stores/choice-list-element.store';
import { ChoiceListStore } from '../../../../stores/choice-list.store';
import { ChoiceListElement } from '../../../../types/choice-list-element.interface';
import { isEntityFormDeleteAction } from '../../../../types/entity-form-result.interface';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ChoiceList } from '../../../../types/choice-list.interface';
import { GenericParentGridComponent } from '../../../generic/generic-parent-grid.component';
import { CesButtonComponent } from '../../../ui/ces-buttons';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

@Component({
  selector: 'app-choice-lists-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesTitleComponent, GenericParentGridComponent, CesButtonComponent, TranslocoModule],
  templateUrl: './choice-lists-list.component.html',
})
export class ChoiceListsListComponent implements OnInit {
  protected readonly choiceListStore = inject(ChoiceListStore);
  protected readonly choiceListElementStore = inject(ChoiceListElementStore);
  private readonly dialogService = inject(EntityDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  choiceListDefinition = choiceListDefinition;
  choiceLists: ChoiceList[] = [];
  elementsByList: Record<number, ChoiceListElement[]> = {};

  dataLoadingCompleted: boolean = false;

  ngOnInit(): void {
    void this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      await this.choiceListStore.loadAll();
      this.choiceLists = this.choiceListStore.entities();

      await this.choiceListElementStore.loadAll();
      const elements = this.choiceListElementStore.entities();

      this.elementsByList = {};
      for (const list of this.choiceLists) {
        this.elementsByList[list.id] = elements
          .filter((element: ChoiceListElement) => element.choiceListId === list.id)
          .sort((a: ChoiceListElement, b: ChoiceListElement) => {
            if (a.sortNr !== null && b.sortNr !== null) {
              return a.sortNr - b.sortNr;
            } else if (a.sortNr !== null) {
              return -1;
            } else if (b.sortNr !== null) {
              return 1;
            }
            return a.name.localeCompare(b.name);
          });
      }
      this.dataLoadingCompleted = true;
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  createChoiceList(): void {
    this.dialogService
      .openCreateDialog<ChoiceList>({
        header: this.choiceListDefinition.messages.dialogTitles.create(),
        entityDefinition: this.choiceListDefinition,
        mode: 'create',
      })
      .subscribe(formData => {
        if (!formData || isEntityFormDeleteAction(formData)) {
          return;
        }
        void (async () => {
          try {
            const name = formData['name'];
            const existingList = name
              ? this.choiceLists.find(list => list.name.toLowerCase().trim() === name.toLowerCase().trim())
              : undefined;

            if (existingList) {
              this.toastService.error('common.errors.choiceListNameExists');
              return;
            }

            const elements = (formData['__elements'] as ChoiceListElement[] | undefined) ?? [];
            delete formData['__elements'];

            formData['hasElement'] = elements.length > 0;
            const createdList = await this.choiceListStore.create(formData);
            if (!createdList) return;

            if (elements.length > 0) {
              await Promise.all(
                elements
                  .filter(element => element.name?.trim())
                  .map(element =>
                    this.choiceListElementStore.create({
                      ...element,
                      choiceListId: createdList.id,
                    }),
                  ),
              );
            }

            await this.loadData();
          } catch (error) {
            console.error(this.translateService.translate('common.errors.choiceListCreationFailed'), error);
          }
        })();
      });
  }
  editChoiceList(list: ChoiceList): void {
    this.dialogService
      .openEditDialog<ChoiceList>(list, {
        header: this.choiceListDefinition.messages.dialogTitles.edit(),
        headerParams: { name: list.name },
        entityDefinition: this.choiceListDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteChoiceList(entity),
      })
      .subscribe(result => {
        if (!result) {
          return;
        }
        if (isEntityFormDeleteAction(result)) {
          this.deleteChoiceList(result.entity);
          return;
        }
        void (async () => {
          try {
            const name = result['name'];
            const existingList = name
              ? this.choiceLists.find(l => l.name.toLowerCase().trim() === name.toLowerCase().trim() && l.id !== list.id)
              : undefined;

            if (existingList) {
              this.toastService.error('common.errors.choiceListNameExists');
              return;
            }

            const elements = (result['__elements'] as ChoiceListElement[] | undefined) ?? [];
            delete result['__elements'];

            result['hasElement'] = elements.length > 0;
            await this.choiceListStore.update(list.id, result);

            const existingElements = this.choiceListElementStore
              .entities()
              .filter((element: ChoiceListElement) => element.choiceListId === list.id);

            if (list.isUsed) {
              const elementsToCreate = elements
                .filter(element => !element.id && element.name?.trim())
                .map(element => ({
                  ...element,
                  choiceListId: list.id,
                }));

              const elementsToUpdate = elements
                .filter(element => element.id && element.name?.trim())
                .map(element => ({
                  ...element,
                  choiceListId: list.id,
                }));

              await Promise.all([
                ...elementsToCreate.map(element => this.choiceListElementStore.create(element)),
                ...elementsToUpdate.map(element => this.choiceListElementStore.update(element.id, element)),
              ]);
            } else {
              const elementsToCreate = elements
                .filter(element => !element.id && element.name?.trim())
                .map(element => ({
                  ...element,
                  choiceListId: list.id,
                }));

              const elementsToUpdate = elements.filter(
                (element: ChoiceListElement) =>
                  element.id && element.name?.trim() && existingElements.some((e: ChoiceListElement) => e.id === element.id),
              );

              const elementsToDelete = existingElements.filter(
                (existingElement: ChoiceListElement) =>
                  !elements.some((element: ChoiceListElement) => element.id === existingElement.id),
              );

              await Promise.all([
                ...elementsToCreate.map((element: ChoiceListElement) => this.choiceListElementStore.create(element)),
                ...elementsToUpdate.map((element: ChoiceListElement) => this.choiceListElementStore.update(element.id, element)),
                ...elementsToDelete.map((element: ChoiceListElement) => this.choiceListElementStore.delete(element.id)),
              ]);
            }

            await this.loadData();
          } catch (error) {
            this.toastService.warning('entities.choiceListElement.messages.deleteError');
            console.error(this.translateService.translate('common.errors.choiceListUpdateFailed'), error);
          }
        })();
      });
  }

  deleteChoiceList(list: ChoiceList): void {
    if (list.isUsed) {
      this.toastService.warning('entities.choiceList.messages.deleteError');
      return;
    }

    void this.choiceListStore
      .delete(list.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.choiceList.messages.deleteError');
        console.error(this.translateService.translate('common.errors.choiceListDeletionFailed'), error);
      });
  }

  createElement(list: ChoiceList): void {
    if (!this.choiceListDefinition.childDefinition) return;

    this.dialogService
      .openCreateDialog<ChoiceListElement>({
        header: this.choiceListDefinition.childDefinition.messages.dialogTitles.create(),
        entityDefinition: this.choiceListDefinition.childDefinition,
        mode: 'create',
        parentId: list.id,
      })
      .subscribe(result => {
        if (result) {
          const elementData = {
            ...result,
            choiceListId: list.id,
          };

          void this.choiceListElementStore.create(elementData).then(() => {
            if (!list.hasElement) {
              void this.choiceListStore.update(list.id, { hasElement: true });
            }
            void this.loadData();
          });
        }
      });
  }

  editElement(element: ChoiceListElement): void {
    if (!this.choiceListDefinition.childDefinition) return;

    this.dialogService
      .openEditDialog<ChoiceListElement>(element, {
        header: this.choiceListDefinition.childDefinition.messages.dialogTitles.edit(),
        headerParams: { name: element.name },
        entityDefinition: this.choiceListDefinition.childDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteElement(entity),
      })
      .subscribe(result => {
        if (!result) {
          return;
        }
        if (isEntityFormDeleteAction(result)) {
          this.deleteElement(result.entity);
          return;
        }
        void this.choiceListElementStore.update(element.id, result).then(() => {
          void this.loadData();
        });
      });
  }

  deleteElement(element: ChoiceListElement): void {
    void this.choiceListElementStore.delete(element.id).then(() => {
      const list = this.choiceLists.find(l => l.id === element.choiceListId);
      if (list) {
        const remainingElements = (this.elementsByList[list.id] ?? []).filter(e => e.id !== element.id);
        if (remainingElements.length === 0 && list.hasElement) {
          void this.choiceListStore.update(list.id, { hasElement: false });
        }
      }
      void this.loadData();
    });
  }
}
